import axiosInstance from '../../services/api';
import { API_ENDPOINTS } from '../feature/riskCalculator/constants';

/**
 * Risk Calculator API Service
 * Handles all API calls related to the Risk Calculator feature
 */
export class RiskCalculatorService {
  /**
   * Fetches the hierarchical culture tree for risk calculator
   * @returns {Promise<Object>} Culture tree data with ARLA hierarchy
   */
  static async getCulturesTree() {
    try {
      const { data } = await axiosInstance.get('/api/cultures/tree/');
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching cultures tree:', error);
      throw error;
    }
  }

  /**
   * Fetches available treatment types (filtered for risk calculator)
   * @returns {Promise<Array>} List of treatment types
   */
  static async getTreatmentTypes() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.TREATMENT_TYPES, {
        params: { display_on_risk_index_calculation: true },
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching treatment types:', error);
      throw error;
    }
  }

  /**
   * Fetches cascading options for risk calculator steps based on previous selections
   * @param {Object} params - Filter parameters
   * @param {string} params.step - Step name: 'treatment_type', 'application_technique', or 'products'
   * @param {Array<string>} [params.cultures] - Selected culture IDs
   * @param {string} [params.treatment_type] - Selected treatment type ID
   * @param {string} [params.application_technique] - Selected application technique ID
   * @returns {Promise<Object>} Options with counts
   */
  static async getCascadingOptions(params) {
    try {
      const queryParams = { ...params };
      if (Array.isArray(queryParams.cultures)) {
        queryParams.cultures = queryParams.cultures.join(',');
      }

      const { data } = await axiosInstance.get('/api/treatments/risk-calculator-options/', {
        params: queryParams,
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching cascading options:', error);
      throw error;
    }
  }

  /**
   * Bulk fetches ALL labels for URL state restoration in ONE call
   * Used when user opens shared URL or refreshes browser
   * Handles partial data gracefully - returns whatever exists
   *
   * @param {Object} ids - IDs to fetch labels for
   * @param {Array<string>} [ids.culture_ids] - Culture IDs to fetch
   * @param {string} [ids.treatment_type_id] - Treatment type ID to fetch
   * @param {string} [ids.application_technique_id] - Application technique ID to fetch
   * @param {Array<string>} [ids.product_ids] - Product IDs to fetch
   * @param {Array<string>} [ids.unit_ids] - Unit IDs to fetch
   * @returns {Promise<Object>} Labels for all provided IDs
   */
  static async bulkInitialize(ids) {
    try {
      const { data } = await axiosInstance.post('/api/treatments/bulk-initialize/', {
        culture_ids: ids.culture_ids || [],
        treatment_type_id: ids.treatment_type_id || null,
        application_technique_id: ids.application_technique_id || null,
        product_ids: ids.product_ids || [],
        unit_ids: ids.unit_ids || [],
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error bulk initializing labels:', error);
      throw error;
    }
  }

  /**
   * Fetches available application techniques (filtered for risk calculator)
   * @param {Object} params - Query parameters
   * @param {string} [params.treatment_type] - Filter by treatment type
   * @returns {Promise<Array>} List of application techniques
   */
  static async getApplicationTechniques(params = {}) {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.APPLICATION_TECHNIQUES, {
        params: {
          ...params,
          display_on_client_site: true,
          display_in_risk_index_calculation: true,
        },
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching application techniques:', error);
      throw error;
    }
  }

  /**
   * Searches commercial products with filters
   * @param {Object} params - Search parameters
   * @param {string} [params.search] - Search term
   * @param {Array<string>} [params.cultures] - Filter by culture IDs
   * @param {string} [params.treatment_type] - Filter by treatment type
   * @returns {Promise<Object>} Search results with products
   */
  static async searchCommercialProducts(params = {}) {
    try {
      const { data } = await axiosInstance.get('/api/commercial-products/search/', { params });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error searching commercial products:', error);
      throw error;
    }
  }

  /**
   * Fetches available units for product dosage
   * @returns {Promise<Array>} List of units
   */
  static async getUnits() {
    try {
      const { data } = await axiosInstance.get(API_ENDPOINTS.UNITS);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching units:', error);
      throw error;
    }
  }

  /**
   * Fetches ALL options for a step without filtering (show all mode)
   * Supports pagination for infinite scroll
   *
   * @param {Object} params - Parameters
   * @param {string} params.step - Step type: 'treatment_type', 'application_technique', 'products', 'units'
   * @param {string} [params.search] - Optional search term
   * @param {number} [params.page=1] - Page number for pagination
   * @param {number} [params.pageSize=50] - Page size
   * @returns {Promise<Object>} { options, hasMore, total }
   */
  static async getAllOptions(params) {
    try {
      // NOTE: `treatmentType` from the params signature is intentionally
      // dropped. Mode C (Voir tous) + Mode D (auto-fallback) must NOT
      // narrow the product list by treatment_type per rule 50. The
      // culture-fallback path (rule 53) lives in the backend ORM, not
      // here. Kept in the public signature for API symmetry; just not
      // destructured locally.
      const { step, search, page = 1, pageSize = 50 } = params;

      // Helper to extract array from various API response formats
      const extractArray = (data) => {
        if (Array.isArray(data)) return data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        if (data?.results && Array.isArray(data.results)) return data.results;
        return [];
      };

      // Rule 55: compute Bio / Fin badges from the raw product payload
      // returned by /api/commercial-products/ when hit in Mode C/D. The
      // cascaded endpoint (Mode A) already emits `badges` so this helper
      // is a no-op there.
      const computeProductBadges = (item) => {
        if (Array.isArray(item.badges) && item.badges.length > 0) {
          return item.badges;
        }
        const badges = [];
        if (item.is_organic_approval) badges.push('Bio');
        const statusName = (
          item.approval_status?.name || item.approval_status || ''
        ).toString().toLowerCase();
        if (statusName.includes('fin') && statusName.includes('homologation')) {
          badges.push('Fin');
        }
        return badges;
      };

      // Helper to transform item to standard format
      // - Units (rule 76/77/78): prefer display_name so "(culture_or_precision)"
      //   shows beside the unit name for non-general units.
      const transformItem = (item, type) => {
        if (type === 'units') {
          return {
            id: item.id,
            name: item.display_name || item.name || item.id,
            is_general: !!item.general_units,
          };
        }
        return {
          id: item.id,
          name: item.name || item.french_name || item.english_name || item.id,
          ...(type === 'products' && {
            approval_number: item.approval_number || '',
            doses: item.doses || [],
            badges: computeProductBadges(item),
          }),
        };
      };

      const endpointConfig = {
        // Rule 35: honor display_on_risk_index_calculation + alphabetical.
        treatment_type: {
          url: API_ENDPOINTS.TREATMENT_TYPES,
          params: {
            display_on_risk_index_calculation: true,
            ordering: 'name',
            ...(search ? { query: search } : {}),
          },
          paginated: false,
        },
        // Rule 41/43 for show-all: keep flag-filtered + priority-then-name.
        application_technique: {
          url: API_ENDPOINTS.APPLICATION_TECHNIQUES,
          params: {
            display_on_client_site: true,
            display_in_risk_index_calculation: true,
            ordering: 'priority,name',
            ...(search ? { query: search } : {}),
          },
          paginated: false,
        },
        // Rule 50 (Mode C "Voir tous les produits") and rule 54 (Mode D
        // auto-fallback on no-match): list every VALID product, alphabetical,
        // NOT narrowed by treatment_type. The spec is explicit:
        //     « Les produits valides n'ont pas à être associés à des traitements. »
        // The `treatment_type` narrowing belongs to Mode B (culture-fallback
        // when the ES cascade is empty — handled inside backend
        // `_products_fallback`, never by this endpoint).
        //
        // "Non disponible commercialement" inclusion (rule 50) is deferred
        // until the status actually exists in the DB.
        products: {
          url: API_ENDPOINTS.COMMERCIAL_PRODUCTS,
          params: {
            page,
            page_size: pageSize,
            ordering: 'name',
            is_fully_valid: true,
            ...(search ? { query: search } : {}),
          },
          paginated: true,
        },
        // Rule 78: general first (negative sort on bool), then alpha.
        units: {
          url: API_ENDPOINTS.UNITS,
          params: {
            ordering: '-general_units,name',
            ...(search ? { query: search } : {}),
          },
          paginated: false,
        },
      };

      const config = endpointConfig[step];
      if (!config) {
        return { options: [], hasMore: false, total: 0 };
      }

      const response = await axiosInstance.get(config.url, { params: config.params });
      const items = extractArray(response.data);
      const options = items.map((item) => transformItem(item, step));

      // Handle pagination info
      const total = response.data?.count || response.data?.total || options.length;
      const hasMore = config.paginated ? page * pageSize < total : false;

      return { options, hasMore, total };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching all options:', error);
      throw error;
    }
  }

  /**
   * Calculates risk indices based on selected parameters
   * @param {Object} payload - Calculator input data
   * @param {Array<string>} payload.cultures - Selected culture IDs (UUIDs)
   * @param {string} payload.treatmentType - Selected treatment type ID (UUID)
   * @param {string} payload.applicationTechnique - Selected application technique ID (UUID)
   * @param {Array<Object>} payload.commercialProducts - Selected products with doses
   * @param {string} payload.commercialProducts[].id - Product ID (UUID)
   * @param {number} payload.commercialProducts[].dose - Product dose
   * @param {string} payload.commercialProducts[].unit - Unit ID (UUID)
   * @returns {Promise<Array>} Calculated risk indices (one result per culture)
   */
  static async calculateRisks(payload) {
    try {
      // Transform frontend payload to backend format
      const backendPayload = {
        cultures: payload.cultures,
        treatment_type: payload.treatmentType,
        technique_or_application_place: payload.applicationTechnique,
        product_items: payload.commercialProducts.map((product) => ({
          commercial_product: product.id,
          max_dose: parseFloat(product.dose),
          units: product.unit,
        })),
      };

      const { data } = await axiosInstance.post(
        API_ENDPOINTS.CALCULATE_RISKS,
        backendPayload
      );
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calculating risks:', error);
      throw error;
    }
  }

  /**
   * Saves a calculator scenario for comparison
   * @param {Object} scenario - Scenario data to save
   * @returns {Promise<Object>} Saved scenario with ID
   */
  static async saveScenario(scenario) {
    try {
      const { data } = await axiosInstance.post('/api/risk-calculator/scenarios/', scenario);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving scenario:', error);
      throw error;
    }
  }

  /**
   * Retrieves saved scenarios for comparison
   * @returns {Promise<Array>} List of saved scenarios
   */
  static async getScenarios() {
    try {
      const { data } = await axiosInstance.get('/api/risk-calculator/scenarios/');
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching scenarios:', error);
      throw error;
    }
  }

  /**
   * Validates all current selections against available options in a single API call.
   * Used for proactive mismatch detection in edit/duplicate mode.
   *
   * @param {Object} selections - Current selections to validate
   * @param {Array<string>} selections.cultures - Selected culture IDs
   * @param {string} [selections.treatmentType] - Selected treatment type ID
   * @param {string} [selections.applicationTechnique] - Selected application technique ID
   * @param {Array<Object>} [selections.commercialProducts] - Selected products with units
   * @returns {Promise<Object>} Validation results for all steps
   */
  static async validateSelections(selections) {
    try {
      const { data } = await axiosInstance.post(API_ENDPOINTS.VALIDATE_SELECTIONS, {
        cultures: selections.cultures || [],
        treatment_type: selections.treatmentType || null,
        application_technique: selections.applicationTechnique || null,
        commercial_products: (selections.commercialProducts || []).map(p => ({
          id: p.id,
          unit: p.unit,
        })),
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error validating selections:', error);
      throw error;
    }
  }
}

/**
 * Query keys factory for React Query cache management
 * Provides consistent keys for caching and invalidation
 */
export const riskCalculatorKeys = {
  all: ['riskCalculator'],
  cultures: () => [...riskCalculatorKeys.all, 'cultures'],
  culturesTree: () => [...riskCalculatorKeys.cultures(), 'tree'],
  treatmentTypes: () => [...riskCalculatorKeys.all, 'treatmentTypes'],
  applicationTechniques: () => [...riskCalculatorKeys.all, 'applicationTechniques'],
  applicationTechnique: (params) => [...riskCalculatorKeys.applicationTechniques(), params],
  products: () => [...riskCalculatorKeys.all, 'products'],
  productsSearch: (params) => [...riskCalculatorKeys.products(), 'search', params],
  units: () => [...riskCalculatorKeys.all, 'units'],
  calculation: (payload) => [...riskCalculatorKeys.all, 'calculation', payload],
  scenarios: () => [...riskCalculatorKeys.all, 'scenarios'],
  validation: (selections) => [...riskCalculatorKeys.all, 'validation', selections],
  allOptions: (step) => [...riskCalculatorKeys.all, 'allOptions', step],
};